import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import appHistory from '../../utils/appHistory';
import entityComposition from '../common/classes/entityComposition';

/**
 * DirectFormCreator - Creates a new direct form and redirects to its editor.
 * Used as a landing page route: /directForms/new/:formType
 */
const DirectFormCreator = _ref => {
  let formActions = _ref.formActions,
    formType = _ref.formType,
    formTypeActions = _ref.formTypeActions,
    user = _ref.user;
  const _useParams = useParams(),
    formTypeIdentifier = _useParams.formType;
  const creatingRef = useRef(false);

  // Load form types on mount
  useEffect(() => {
    if (formTypeActions && formType !== null && formType !== void 0 && formType.indexQuery) {
      formTypeActions.serverIndex(formType.indexQuery);
    }
  }, [formTypeActions, formType === null || formType === void 0 ? void 0 : formType.indexQuery]);

  // Create form types lookup
  const formTypesByIdentifier = useMemo(() => {
    const fts = {};
    if (formType !== null && formType !== void 0 && formType.list) {
      formType.list.forEach(ft => {
        fts[ft.identifier] = ft;
      });
    }
    return fts;
  }, [formType]);

  // Create form and redirect when ready
  useEffect(() => {
    const createAndRedirect = async () => {
      // Prevent double creation
      if (creatingRef.current) return;

      // Wait for dependencies (formTypesByIdentifier populated when form types load)
      if (!formActions || !(user !== null && user !== void 0 && user.user_id) || !Object.keys(formTypesByIdentifier).length) return;
      const currentFormType = formTypesByIdentifier[formTypeIdentifier];
      if (!currentFormType) return;
      creatingRef.current = true;
      try {
        const formID = uuidV4().toUpperCase();
        const createdDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        await formActions.create(formID, createdDate, user.user_id, currentFormType);
        await formActions.select(formID);
        appHistory.replace("/directForms/".concat(formID));
      } catch (error) {
        // On error, redirect to home
        creatingRef.current = false;
        appHistory.replace('/');
      }
    };
    createAndRedirect();
  }, [formActions, formTypesByIdentifier, formTypeIdentifier, user]);

  // Show loading state while creating
  return /*#__PURE__*/React.createElement("div", {
    className: "text-center",
    style: {
      padding: '50px'
    }
  }, /*#__PURE__*/React.createElement("i", {
    className: "fa fa-spinner fa-spin fa-2x"
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      marginTop: '10px'
    }
  }, "Creating..."));
};
DirectFormCreator.propTypes = {
  formActions: PropTypes.shape({
    create: PropTypes.func,
    select: PropTypes.func
  }).isRequired,
  formType: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({
      identifier: PropTypes.string
    }))
  }).isRequired,
  formTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  user: PropTypes.shape({
    user_id: PropTypes.string
  }).isRequired
};
export { DirectFormCreator as TestDirectFormCreator };
export default entityComposition(DirectFormCreator, ['formType'], {
  user: state => state.login.user
}, ['form', 'formType']);